import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import useHeader from '@hooks/useHeader';
import { GroupsRounded, TaskAltRounded } from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Icon,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from '@tanstack/react-router';
import EditGroup from '../EditGroup';
import Filters from '@Components/Specific/Filters/Filters';
import { contactFilters } from '@Components/Specific/Filters/filterSets';
import useFilters from '@hooks/useFilters';
import GroupAvatar from '../Components/GroupAvatar';
import Tag from '@Components/Common/Tag';
import { groupIemStatus } from '@data/statuses';
import useDialog from '@hooks/useDialog';
import { contactsGroupQueryOptions } from '@utils/queryOptions';

import GroupContacts from './group-contacts';
import NoGroupContacts from './no-contacts';
import { useIntersectionObserver } from '@uidotdev/usehooks';
import { closeOptions } from '@utils/utils';

const GroupFilters = ({ filters, addFilter, removeFilter, setFilter }) => {
    const { t, i18n } = useTranslation();
    const boxRef = useRef();
    const [filtersRef, entry] = useIntersectionObserver({
        root: boxRef.current,
    });
    const [expanded, setExpanded] = useState(false);

    const expandControl = (expanded ||
        (!entry?.isIntersecting && !expanded)) && (
        <Link
            variant="bodyMedium"
            sx={{
                fontWeight: 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                alignSelf: 'center',
                position: 'relative',
                ...(!expanded
                    ? {
                          '::before': {
                              content: '""',
                              position: 'absolute',
                              top: -8,
                              bottom: -8,
                              left: -20,
                              width: 16,
                              background: theme =>
                                  `linear-gradient(to right, rgba(255, 255, 255, 0), ${theme.palette.surface.main})`,
                              pointerEvents: 'none',
                          },
                      }
                    : {}),
            }}
            onClick={() => setExpanded(!expanded)}
        >
            {entry?.isIntersecting ? t('Show Less') : t('Show More')}
        </Link>
    );

    return (
        <Stack gap={0.5}>
            <Typography variant="labelLarge" color="textSecondary" gutterBottom>
                {t('Auto-Grouping')}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.5}>
                <Box ref={boxRef} display="flex" overflow="hidden">
                    <Box ref={filtersRef}>
                        <Filters
                            filterSet={contactFilters}
                            addFilter={addFilter}
                            filters={filters}
                            removeFilter={removeFilter}
                            wrapFilters
                            fullWidth={!expanded}
                            filtersExtention={expanded ? expandControl : null}
                            setFilter={setFilter}
                        />
                    </Box>
                </Box>
                {!expanded && expandControl}
            </Stack>
        </Stack>
    );
};

const ContactsGroupDetails = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { groupId } = useParams({});
    const dialog = useDialog();
    const [editGroup, setEditGroup] = useState(false);

    const { data: group, refetch: refetchGroup } = useSuspenseQuery(
        contactsGroupQueryOptions(groupId),
    );

    const headerProps = useMemo(() => {
        return {
            title: t('Groups'),
            searchProps: {
                searchPlacholder: t('search:group-contacts'),
            },
            titleSuffix: group?.name,
        };
    }, []);

    useHeader(headerProps);

    const deleteGroupMutation = useMutation({
        mutationKey: ['deleteContactsGroup'],
        onSuccess: () => {
            dialog.close();
            navigate({ to: '..' });
        },
    });

    const { mutate: updateGroup } = useMutation({
        mutationKey: ['updateContactsGroup'],
        onSuccess: () => {
            refetchGroup();
            closeOptions();
        },
    });

    const { filters, addFilter, removeFilter, setFilter } = useFilters({
        filters: group?.filter_criteria || {},
        onChange: filters =>
            updateGroup([groupId, { filter_criteria: filters }]),
    });

    const groupOptions: QuickOption[] = [
        {
            label: t('Edit'),
            onClick: () => {
                setEditGroup(true);
                closeOptions();
            },
            Icon: props => <Icon {...props}>edit_square</Icon>,
        },
        {
            label: t('Delete Group'),
            onClick: () =>
                dialog.open({
                    title: t('Delete Group') + '?',
                    content: t('prompts:content.deleteGroup'),
                    confirmMutation: deleteGroupMutation,
                    onConfirm: () => groupId,
                    confirmLabel: t('Delete'),
                    confirmButtonProps: { color: 'error' },
                }),
            Icon: GroupsRounded,
            sx: { color: 'error.main' },
        },
    ];

    return (
        <Box flexGrow={1}>
            <Box display="flex" flexDirection="column" height="100%">
                <Card sx={{ mb: 4.5 }}>
                    <CardContent>
                        <Box display="flex" gap={2}>
                            <Stack alignItems="center" gap={2}>
                                <GroupAvatar group={group} size="large" />
                            </Stack>

                            <Stack spacing={2} flexGrow={1} overflow="auto">
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="start"
                                >
                                    <Box mt={0.5}>
                                        <Box display="flex" gap={1}>
                                            <Typography variant="headlineSmall">
                                                {group.name}
                                            </Typography>
                                            {group.todo && (
                                                <Tag
                                                    label={t('Task Group')}
                                                    color="primary"
                                                    size="small"
                                                    variant="tonal"
                                                    sx={{ height: 26 }}
                                                    icon={<TaskAltRounded />}
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="bodyLarge">
                                            {group.description}
                                        </Typography>
                                    </Box>
                                    <QuickOptions options={groupOptions} />
                                </Box>
                            </Stack>
                        </Box>
                        {/* <todo-groups>
                            <Divider sx={{ my: 1.5 }} />
                            <GroupFilters
                                filters={filters}
                                addFilter={addFilter}
                                removeFilter={removeFilter}
                                setFilter={setFilter}
                            />
                        </todo-groups> */}
                    </CardContent>
                </Card>
                {Object.values(group.status_counts).length ? (
                    <GroupContacts group={group} refetchGroup={refetchGroup} />
                ) : (
                    <NoGroupContacts />
                )}
                <EditGroup
                    open={editGroup}
                    onClose={() => {
                        refetchGroup();
                        setEditGroup(false);
                        closeOptions();
                    }}
                    group={group}
                />
            </Box>
        </Box>
    );
};

export default ContactsGroupDetails;
